import { useLazyQuery } from '@apollo/client';
import { useAtomValue } from 'jotai';
import { useState, useCallback } from 'react';
import { GET_LATEST_SNAPSHOT_ID } from '~/graphql/queries/portfolio';
import { latestSnapshotIdAtom, portfolioIdAtom } from '~/atoms/dashboardAtom';

export const useSnapshotVersionCheck = () => {
  const currentSnapshotId = useAtomValue(latestSnapshotIdAtom);
  const portfolioId = useAtomValue(portfolioIdAtom);
  const [showWarning, setShowWarning] = useState(false);

  const [getLatestSnapshot, { data: latestSnapshotData }] = useLazyQuery(
    GET_LATEST_SNAPSHOT_ID,
    {
      fetchPolicy: 'network-only',
    },
  );

  const latestSnapshotId =
    latestSnapshotData?.EQPortfolio?.[0]?.EQPortfolioSnapshots?.[0]?.id;

  const dismissWarning = () => {
    setShowWarning(false);
  };

  const refreshToLatest = async () => {
    try {
      const { data: freshSnapshotData } = await getLatestSnapshot({
        variables: { portfolioId },
      });
      const freshLatestSnapshotId =
        freshSnapshotData?.EQPortfolio?.[0]?.EQPortfolioSnapshots?.[0]?.id;

      if (freshLatestSnapshotId) {
        setShowWarning(false);
        return freshLatestSnapshotId;
      }
      return null;
    } catch (error) {
      console.error('Failed to refresh to latest snapshot:', error);
      return null;
    }
  };

  // Manual check function for when user visits dashboard
  const checkIfOutdated = useCallback(async () => {
    try {
      const { data: freshSnapshotData } = await getLatestSnapshot({
        variables: { portfolioId },
      });
      const freshLatestSnapshotId =
        freshSnapshotData?.EQPortfolio?.[0]?.EQPortfolioSnapshots?.[0]?.id;

      if (
        currentSnapshotId &&
        freshLatestSnapshotId &&
        currentSnapshotId !== freshLatestSnapshotId
      ) {
        setShowWarning(true);
      }
    } catch (error) {
      console.error('Failed to check latest snapshot:', error);
    }
  }, [currentSnapshotId, getLatestSnapshot, portfolioId]);

  // Check and update to latest snapshot before analysis
  const checkAndUpdateBeforeAnalysis = useCallback(async () => {
    try {
      const { data: freshSnapshotData } = await getLatestSnapshot({
        variables: { portfolioId },
      });
      const freshLatestSnapshotId =
        freshSnapshotData?.EQPortfolio?.[0]?.EQPortfolioSnapshots?.[0]?.id;

      if (
        freshLatestSnapshotId &&
        freshLatestSnapshotId !== currentSnapshotId
      ) {
        // Show warning about outdated snapshot
        setShowWarning(true);

        // Return current snapshot ID to prevent analysis with outdated data
        // User will need to click "Refresh to Latest" button to proceed
        return null;
      }

      return currentSnapshotId;
    } catch (error) {
      console.error('Failed to check latest snapshot:', error);
      return currentSnapshotId;
    }
  }, [getLatestSnapshot, currentSnapshotId, portfolioId]);

  return {
    showWarning,
    latestSnapshotId,
    checkIfOutdated,
    dismissWarning,
    refreshToLatest,
    checkAndUpdateBeforeAnalysis,
  };
};
